import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';

export default function AdsTxtPage() {
	const params = useParams<{ service?: string }>();
	const navigate = useNavigate();
	const services = useQuery({ queryKey: ['services'], queryFn: api.services });

	const activeService = useMemo(() => {
		if (params.service) return params.service;
		return services.data?.find((s) => s.enabled)?.code;
	}, [params.service, services.data]);

	useEffect(() => {
		if (!params.service && activeService) {
			navigate(`/adstxt/${activeService}`, { replace: true });
		}
	}, [params.service, activeService, navigate]);

	return (
		<div className="flex flex-col gap-4">
			{services.data && activeService && (
				<Tabs value={activeService} onValueChange={(v) => navigate(`/adstxt/${v}`)}>
					<TabsList>
						{services.data.map((s) => (
							<TabsTrigger key={s.code} value={s.code} disabled={!s.enabled}>
								{s.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			)}
			{activeService ? (
				<AdsTxtEditor service={activeService} key={activeService} />
			) : (
				<p className="text-sm text-muted-foreground">Brak skonfigurowanych serwisów.</p>
			)}
		</div>
	);
}

type Toast = { kind: 'ok' | 'err'; message: string } | null;

function AdsTxtEditor({ service }: { service: string }) {
	const queryClient = useQueryClient();
	const [toast, setToast] = useState<Toast>(null);
	const [draft, setDraft] = useState<string | null>(null);
	const areaRef = useRef<HTMLTextAreaElement>(null);

	// Nowe wpisy dokleja się na końcu ads.txt — po wczytaniu treści ustaw kursor
	// na końcu i przewiń do dołu, żeby od razu dopisywać we właściwym miejscu.
	const scrollToEnd = () => {
		const el = areaRef.current;
		if (!el) return;
		el.focus();
		el.setSelectionRange(el.value.length, el.value.length);
		el.scrollTop = el.scrollHeight;
	};

	// Treść bierzemy wyłącznie z KV (klucz adstxt:<service>) — początkowa wartość
	// trafia tam z seeda; API WP nie jest już odpytywane z panelu.
	const stored = useQuery({
		queryKey: ['adstxt', service],
		queryFn: () => api.adstxt.get(service),
	});

	const baseline = stored.data?.content ?? '';
	const value = draft ?? baseline;
	const dirty = draft !== null && draft !== baseline;
	const lineCount = value ? value.split('\n').length : 0;

	// Po dociągnięciu treści z KV zjedź na koniec.
	useEffect(() => {
		if (baseline) scrollToEnd();
	}, [baseline]);

	// Purge musi być widoczny, a nie tylko w logach workera — cichy błąd zostawiał
	// bizblogowi ads.txt sprzed 18 dni.
	const purgeToast = (failed: string[] | undefined, ok: string, what: string): Toast =>
		failed?.length
			? { kind: 'err', message: `${what}: Cloudflare odrzucił purge (${failed.join('; ')}) — brzeg dalej serwuje stary plik.` }
			: { kind: 'ok', message: ok };

	const save = useMutation({
		mutationFn: () => api.adstxt.put(service, value),
		onSuccess: (saved) => {
			queryClient.setQueryData(['adstxt', service], saved);
			setDraft(null);
			setToast(purgeToast(saved.purgeFailed, 'Zapisano ads.txt.', 'Zapisano'));
		},
		onError: (e) => setToast({ kind: 'err', message: (e as Error).message }),
	});

	const purge = useMutation({
		mutationFn: () => api.adstxt.purge(service),
		onSuccess: ({ purgeFailed }) =>
			setToast(purgeToast(purgeFailed, 'Wyczyszczono cache Cloudflare.', 'Purge')),
		onError: (e) => setToast({ kind: 'err', message: (e as Error).message }),
	});

	return (
		<div className="flex flex-col gap-4">
			{toast && (
				<div
					className={
						'rounded-md border px-3 py-2 text-sm ' +
						(toast.kind === 'ok'
							? 'border-emerald-200 bg-emerald-50 text-emerald-700'
							: 'border-destructive/30 bg-destructive/10 text-destructive')
					}
				>
					{toast.message}
				</div>
			)}

			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="text-xs text-muted-foreground">
					{stored.data ? (
						<>
							Zapisano przez {stored.data.updatedBy} · {new Date(stored.data.updatedAt).toLocaleString('pl-PL')} ·{' '}
							{lineCount} linii
						</>
					) : stored.isSuccess ? (
						<>Brak wpisu w KV dla tego serwisu — zaseeduj treść startową.</>
					) : (
						'Ładowanie…'
					)}
				</div>
				<div className="flex gap-2">
					<Button size="sm" variant="outline" onClick={() => purge.mutate()} disabled={purge.isPending}>
						{purge.isPending ? 'Czyszczenie…' : 'Wyczyść cache'}
					</Button>
					<Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
						{save.isPending ? 'Zapisywanie…' : 'Zapisz'}
					</Button>
				</div>
			</div>

			{stored.isError && <p className="text-sm text-destructive">Błąd: {String(stored.error)}</p>}

			<Card className="py-0">
				<CardContent className="p-0">
					<Textarea
						ref={areaRef}
						value={value}
						onChange={(e) => setDraft(e.target.value)}
						spellCheck={false}
						className="h-[38vh] max-h-[520px] min-h-[240px] resize-y rounded-md border-0 font-mono text-xs leading-relaxed"
						placeholder="adagio.io,1071,RESELLER"
					/>
				</CardContent>
			</Card>
		</div>
	);
}
